import React from 'react';
import {
  Shield, Lock, Eye, Trash2, FileText, Users,
  Share2, Clock, AlertCircle, Cookie,
  Globe, Smartphone, Key, Server
} from 'lucide-react';

interface PrivacyPolicyProps {
  isOpen: boolean;
  onClose: () => void;
}

const PrivacyPolicy: React.FC<PrivacyPolicyProps> = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div style={styles.header}>
          <Shield size={28} style={{ color: '#6a11cb' }} />
          <h2 style={styles.title}>Политика обработки персональных данных</h2>
          <button style={styles.closeButton} onClick={onClose}>✕</button>
        </div>

        <div style={styles.content}>
          <div style={styles.lastUpdated}>Последнее обновление: 06 октября 2026 г.</div>

          {/* 1. ОБЩИЕ ПОЛОЖЕНИЯ */}
          <div style={styles.section}>
            <div style={styles.sectionIcon}><FileText size={20} /></div>
            <h3 style={styles.sectionTitle}>1. Общие положения</h3>
          </div>
          <p style={styles.text}>
            Настоящая Политика обработки персональных данных (далее — Политика) разработана в соответствии
            с Федеральным законом от 27.07.2006 № 152-ФЗ «О персональных данных» и определяет порядок
            обработки персональных данных и меры по обеспечению безопасности персональных данных,
            предпринимаемые <strong>Индивидуальным предпринимателем Наймушиной Евгенией Вадимовной</strong>{' '}
            (ИНН 773379346085, ОГРНИП 323784700299018), сокращенно — <strong>ИП Наймушина Е.В.</strong>{' '}
            (далее — Оператор).
          </p>
          <p style={styles.text}>
            Адрес: 197375, г. Санкт-Петербург, вн. тер. м.о. Коломяги, наб. Реки Каменки, д. 19, к. 1, стр. 1, кв. 341<br />
            Сайт: <a href="https://hrlama.ru" style={styles.link}>hrlama.ru</a><br />
            Email: <a href="mailto:info@hrlama.ru" style={styles.link}>info@hrlama.ru</a>
          </p>

          {/* 2. ОСНОВНЫЕ ПОНЯТИЯ */}
          <div style={styles.section}>
            <div style={styles.sectionIcon}><Users size={20} /></div>
            <h3 style={styles.sectionTitle}>2. Основные понятия</h3>
          </div>
          <ul style={styles.list}>
            <li><strong>Персональные данные (ПДн)</strong> — любая информация, относящаяся к прямо или косвенно определенному или определяемому физическому лицу (субъекту ПДн).</li>
            <li><strong>Оператор</strong> — ИП Наймушина Е.В., организующая и осуществляющая обработку ПДн.</li>
            <li><strong>Обработка ПДн</strong> — любое действие (операция) или совокупность действий с ПДн, включая сбор, запись, систематизацию, накопление, хранение, уточнение, извлечение, использование, передачу, обезличивание, блокирование, удаление, уничтожение.</li>
            <li><strong>Платформа</strong> — HR-платформа «HR LAMA», включая веб-версию, мобильное приложение и браузерное расширение, размещённые по адресу <a href="https://hrlama.ru" style={styles.link}>hrlama.ru</a>.</li>
            <li><strong>Пользователь</strong> — лицо, использующее Платформу и предоставившее свои ПДн Оператору.</li>
          </ul>

          {/* 3. СУБЪЕКТЫ ПДн */}
          <div style={styles.section}>
            <div style={styles.sectionIcon}><Users size={20} /></div>
            <h3 style={styles.sectionTitle}>3. Категории субъектов ПДн</h3>
          </div>
          <p style={styles.text}>Платформа обрабатывает ПДн следующих категорий субъектов:</p>
          <ul style={styles.list}>
            <li><strong>Кандидаты</strong> — лица, откликнувшиеся на вакансии, загрузившие резюме или прошедшие психометрические тесты;</li>
            <li><strong>Сотрудники</strong> — лица, состоящие в трудовых отношениях с организациями-клиентами;</li>
            <li><strong>Пользователи Платформы</strong> — HR-специалисты, рекрутеры, руководители, бухгалтеры, sales-менеджеры, администраторы;</li>
            <li><strong>Посетители сайта</strong> — лица, взаимодействующие с лендингом, формами и виджетами.</li>
          </ul>

          {/* 4. КАТЕГОРИИ ПДн */}
          <div style={styles.section}>
            <div style={styles.sectionIcon}><Eye size={20} /></div>
            <h3 style={styles.sectionTitle}>4. Категории обрабатываемых ПДн</h3>
          </div>
          <p style={styles.text}>Оператор обрабатывает следующие категории ПДн:</p>
          <ul style={styles.list}>
            <li><strong>Общие ПДн:</strong> фамилия, имя, отчество, дата рождения, гражданство, адрес электронной почты, номер телефона, место работы, должность, наименование компании.</li>
            <li><strong>Профессиональные ПДн:</strong> образование, опыт работы, профессиональные навыки, зарплатные ожидания.</li>
            <li><strong>Специальные категории ПДн:</strong> результаты психологических тестов (DISC, Басс-Дарки, КОС, УСК, ДДО, мотивационные и профессиональные тесты), данные о профессиональной пригодности, результаты оценки компетенций.</li>
            <li><strong>Биометрические ПДн:</strong> фотографии пользователей (только при добровольной загрузке).</li>
            <li><strong>Технические ПДн:</strong> IP-адрес, cookies, данные о браузере и устройстве, данные веб-аналитики.</li>
            <li><strong>Платёжные ПДн:</strong> реквизиты для биллинга (обрабатываются платёжными провайдерами ЮKassa и Альфа-Банк).</li>
            <li><strong>Данные из внешних источников:</strong> резюме, импортированные с hh.ru, superjob.ru, avito.ru через браузерное расширение.</li>
          </ul>

          {/* 5. МОДУЛИ ПЛАТФОРМЫ */}
          <div style={styles.section}>
            <div style={styles.sectionIcon}><Server size={20} /></div>
            <h3 style={styles.sectionTitle}>5. Модули Платформы и обработка ПДн</h3>
          </div>
          <p style={styles.text}>ПДн обрабатываются в следующих модулях Платформы:</p>
          <ul style={styles.list}>
            <li><strong>HR-цифровой хаб:</strong> канбан кандидатов, вакансии, сотрудники, заявки на подбор;</li>
            <li><strong>Рекрутинг:</strong> воронка подбора, аналитика, талант-пул, интервью-скрипты;</li>
            <li><strong>E-Learning:</strong> тесты, курсы, бенефиты, рейтинги;</li>
            <li><strong>BI-дашборды:</strong> аналитика (преимущественно обезличенные данные);</li>
            <li><strong>KPI:</strong> оценка эффективности сотрудников;</li>
            <li><strong>Психометрика:</strong> DISC, Басс-Дарки, КОС, УСК, ДДО;</li>
            <li><strong>Парсинг резюме:</strong> извлечение ФИО, email, телефона, должности, зарплаты;</li>
            <li><strong>Конвертация PDF→Word:</strong> обработка загружаемых документов;</li>
            <li><strong>Внутренний чат (мессенджер):</strong> обмен сообщениями между сотрудниками;</li>
            <li><strong>Email:</strong> шаблоны, массовые рассылки, CRM-переписка;</li>
            <li><strong>Задачи:</strong> канбан, календарь, диаграмма Ганта;</li>
            <li><strong>Платежи и биллинг</strong> (ЮKassa, Альфа-Банк);</li>
            <li><strong>Интеграция с HeadHunter (hh.ru);</strong></li>
            <li><strong>Бухгалтерия и кадровый документооборот;</strong></li>
            <li><strong>Аудит действий с ПДн.</strong></li>
          </ul>

          {/* 6. ЦЕЛИ ОБРАБОТКИ */}
          <div style={styles.section}>
            <div style={styles.sectionIcon}><Lock size={20} /></div>
            <h3 style={styles.sectionTitle}>6. Цели обработки ПДн</h3>
          </div>
          <p style={styles.text}>Оператор обрабатывает ПДн Пользователей в следующих целях:</p>
          <ul style={styles.list}>
            <li>Регистрация и аутентификация Пользователей на Платформе;</li>
            <li>Предоставление доступа к функционалу HR-платформы «HR LAMA»;</li>
            <li>Ведение кадрового резерва и управление процессами подбора персонала;</li>
            <li>Проведение оценки, тестирования и формирование аналитических отчётов;</li>
            <li>Обучение и развитие сотрудников (E-Learning);</li>
            <li>Психометрическая диагностика (DISC, Басс-Дарки, КОС, УСК, ДДО);</li>
            <li>Внутренние коммуникации (чат, email);</li>
            <li>Управление задачами и проектами;</li>
            <li>Обработка платежей и биллинг;</li>
            <li>Синхронизация с HeadHunter (hh.ru);</li>
            <li>Бухгалтерский и кадровый документооборот;</li>
            <li>Аудит действий с ПДн;</li>
            <li>Оказание услуг по внедрению и технической поддержке;</li>
            <li>Направление уведомлений, связанных с использованием Платформы;</li>
            <li>Обработка запросов и обращений Пользователей;</li>
            <li>Улучшение качества работы Платформы и предоставляемых услуг.</li>
          </ul>

          {/* 7. ПРАВОВЫЕ ОСНОВАНИЯ */}
          <div style={styles.section}>
            <div style={styles.sectionIcon}><Shield size={20} /></div>
            <h3 style={styles.sectionTitle}>7. Правовые основания обработки ПДн</h3>
          </div>
          <ul style={styles.list}>
            <li>Федеральный закон от 27.07.2006 № 152-ФЗ «О персональных данных»;</li>
            <li>Трудовой кодекс РФ (для обработки ПДн сотрудников);</li>
            <li>Согласие субъекта ПДн на обработку его персональных данных (предоставляется при регистрации на Платформе);</li>
            <li>Договор (пользовательское соглашение), заключаемый между Оператором и Пользователем;</li>
            <li>Локальные акты Оператора.</li>
          </ul>

          {/* 8. СОГЛАСИЕ */}
          <div style={styles.section}>
            <div style={styles.sectionIcon}><FileText size={20} /></div>
            <h3 style={styles.sectionTitle}>8. Согласие на обработку ПДн</h3>
          </div>
          <p style={styles.text}>
            Согласие на обработку ПДн предоставляется субъектом ПДн путём проставления соответствующей
            отметки при регистрации на Платформе, при заполнении форм обратной связи, при загрузке резюме
            или при прохождении психометрических тестов.
          </p>
          <p style={styles.text}>
            Согласие может быть отозвано в любое время путём направления письменного обращения на email{' '}
            <a href="mailto:info@hrlama.ru" style={styles.link}>info@hrlama.ru</a> или через личный кабинет.
            После отзыва согласия Оператор прекращает обработку и уничтожает ПДн в течение 30 дней,
            за исключением случаев, когда обработка должна быть продолжена в силу закона.
          </p>

          {/* 9. МЕРЫ ЗАЩИТЫ */}
          <div style={styles.section}>
            <div style={styles.sectionIcon}><Lock size={20} /></div>
            <h3 style={styles.sectionTitle}>9. Меры защиты ПДн</h3>
          </div>
          <ul style={styles.list}>
            <li><strong>Шифрование данных:</strong> пароли хешируются с использованием bcrypt (12 раундов соли); чувствительные поля ПДн шифруются с применением алгоритма AES-256-GCM.</li>
            <li><strong>Защита каналов связи:</strong> передача данных осуществляется по защищённому протоколу HTTPS с использованием SSL/TLS сертификатов.</li>
            <li><strong>Аутентификация:</strong> JWT-токены + Refresh-токены + двухфакторная аутентификация (2FA).</li>
            <li><strong>Разграничение доступа:</strong> реализована ролевая модель доступа (RBAC); каждый Пользователь имеет доступ только к своим данным.</li>
            <li><strong>Защита от НСД:</strong> применяются защитные HTTP-заголовки (Helmet, Content Security Policy, X-Frame-Options, X-Content-Type-Options).</li>
            <li><strong>Защита от атак:</strong> настроена защита от NoSQL-инъекций, XSS-атак, CSRF-атак, HPP, брутфорс-атак.</li>
            <li><strong>Rate limiting:</strong> ограничение частоты запросов к API на трёх уровнях (общий, авторизация, загрузка файлов).</li>
            <li><strong>Журналирование:</strong> ведётся аудит доступа к ПДн с фиксацией времени, IP-адреса, пользователя и типа операции.</li>
            <li><strong>Резервное копирование:</strong> ежедневное автоматическое резервное копирование базы данных (prod 03:00, dev 04:00, ротация еженедельно).</li>
            <li><strong>Мониторинг безопасности:</strong> непрерывный мониторинг состояния сервисов.</li>
            <li><strong>Физическая безопасность:</strong> серверы расположены на территории Российской Федерации в сертифицированных дата-центрах.</li>
            <li><strong>Хранение секретов:</strong> секреты хранятся в защищённых конфигурационных файлах (права 600), исключён доступ извне.</li>
          </ul>

          {/* 10. ПРАВА СУБЪЕКТОВ */}
          <div style={styles.section}>
            <div style={styles.sectionIcon}><Trash2 size={20} /></div>
            <h3 style={styles.sectionTitle}>10. Права субъектов ПДн</h3>
          </div>
          <ul style={styles.list}>
            <li>Получать информацию, касающуюся обработки его ПДн (через личный кабинет или по запросу на email Оператора);</li>
            <li>Требовать уточнения, блокирования или уничтожения своих ПДн;</li>
            <li>Отозвать согласие на обработку ПДн в любое время;</li>
            <li>Получить копию своих ПДн в машиночитаемом формате (экспорт);</li>
            <li>Обжаловать действия или бездействие Оператора в Роскомнадзор или в судебном порядке.</li>
          </ul>
          <p style={styles.text}>
            📧 Для реализации прав обращайтесь: <a href="mailto:info@hrlama.ru" style={styles.link}>info@hrlama.ru</a>.
            Срок ответа — не более 30 дней с момента получения запроса.
          </p>

          {/* 11. СРОКИ ХРАНЕНИЯ */}
          <div style={styles.section}>
            <div style={styles.sectionIcon}><Clock size={20} /></div>
            <h3 style={styles.sectionTitle}>11. Сроки обработки и хранения ПДн</h3>
          </div>
          <ul style={styles.list}>
            <li><strong>Кандидаты:</strong> до 6 месяцев после закрытия вакансии, либо до отзыва согласия;</li>
            <li><strong>Талант-пул:</strong> до 12 месяцев с момента последнего отклика;</li>
            <li><strong>Сотрудники:</strong> в соответствии с ТК РФ и требованиями к кадровому учёту;</li>
            <li><strong>Пользователи Платформы:</strong> до удаления учётной записи;</li>
            <li><strong>Технические данные (логи, cookies):</strong> до 12 месяцев;</li>
            <li><strong>Платёжные данные:</strong> в соответствии с требованиями бухгалтерского учёта (5 лет).</li>
          </ul>
          <p style={styles.text}>
            По истечении срока хранения ПДн уничтожаются в течение 30 дней.
          </p>

          {/* 12. ПЕРЕДАЧА ТРЕТЬИМ ЛИЦАМ */}
          <div style={styles.section}>
            <div style={styles.sectionIcon}><Share2 size={20} /></div>
            <h3 style={styles.sectionTitle}>12. Передача ПДн третьим лицам</h3>
          </div>
          <p style={styles.text}>
            Для обеспечения работы Платформы ПДн могут передаваться следующим третьим лицам исключительно
            в рамках оказания услуг и на основании договоров, содержащих требования к защите ПДн в соответствии с 152-ФЗ:
          </p>
          <ul style={styles.list}>
            <li><strong>HeadHunter (hh.ru)</strong> — синхронизация вакансий и откликов;</li>
            <li><strong>SuperJob, Avito</strong> — импорт резюме через браузерное расширение;</li>
            <li><strong>ЮKassa, Альфа-Банк</strong> — обработка платежей;</li>
            <li><strong>SMTP-провайдер</strong> — отправка email-рассылок;</li>
            <li><strong>Expo</strong> — push-уведомления в мобильном приложении;</li>
            <li><strong>Яндекс.Метрика, VK Pixel, Google Analytics, FB Pixel</strong> — веб-аналитика (в обезличенном виде).</li>
          </ul>
          <p style={styles.text}>
            Оператор не передаёт ПДн иным третьим лицам, за исключением случаев, предусмотренных
            законодательством РФ (запросы уполномоченных органов и т. п.).
          </p>

          {/* 13. COOKIES */}
          <div style={styles.section}>
            <div style={styles.sectionIcon}><Cookie size={20} /></div>
            <h3 style={styles.sectionTitle}>13. Cookies и веб-аналитика</h3>
          </div>
          <p style={styles.text}>Платформа использует следующие типы cookies:</p>
          <ul style={styles.list}>
            <li><strong>Обязательные cookies</strong> — для работы авторизации, поддержания сессии и корректной работы интерфейса;</li>
            <li><strong>Аналитические cookies</strong> — Яндекс.Метрика, VK Pixel, Google Analytics, FB Pixel — для анализа посещаемости и улучшения Платформы.</li>
          </ul>
          <p style={styles.text}>
            Вы можете отключить cookies в настройках браузера. При отключении обязательных cookies
            работа Платформы может быть нарушена.
          </p>

          {/* 14. ТРАНСГРАНИЧНАЯ ПЕРЕДАЧА */}
          <div style={styles.section}>
            <div style={styles.sectionIcon}><Globe size={20} /></div>
            <h3 style={styles.sectionTitle}>14. Трансграничная передача ПДн</h3>
          </div>
          <p style={styles.text}>
            Трансграничная передача ПДн за пределы территории РФ осуществляется только при наличии
            письменного согласия субъекта ПДн или в иных случаях, предусмотренных законодательством РФ.
          </p>
          <p style={styles.text}>
            Основная инфраструктура Платформы размещена на серверах в Российской Федерации и соответствует
            требованиям 152-ФЗ о локализации ПДн.
          </p>

          {/* 15. МОБИЛЬНОЕ ПРИЛОЖЕНИЕ */}
          <div style={styles.section}>
            <div style={styles.sectionIcon}><Smartphone size={20} /></div>
            <h3 style={styles.sectionTitle}>15. Мобильное приложение и расширение</h3>
          </div>
          <p style={styles.text}>
            <strong>Мобильное приложение HR LAMA (Android)</strong> обрабатывает ПДн в том же объёме,
            что и веб-версия. Приложение использует push-уведомления через сервис Expo. Уведомления
            можно отключить в настройках устройства.
          </p>
          <p style={styles.text}>
            <strong>Браузерное расширение</strong> для Chrome/Yandex используется для импорта резюме
            с внешних сайтов (hh.ru, superjob.ru, avito.ru). Расширение получает доступ только к тем
            данным, которые пользователь явно передаёт в Платформу.
          </p>

          {/* 16. ИЗМЕНЕНИЯ ПОЛИТИКИ */}
          <div style={styles.section}>
            <div style={styles.sectionIcon}><AlertCircle size={20} /></div>
            <h3 style={styles.sectionTitle}>16. Изменения Политики</h3>
          </div>
          <p style={styles.text}>
            Оператор вправе изменять настоящую Политику. Актуальная редакция публикуется на странице{' '}
            <a href="https://hrlama.ru/privacy.html" style={styles.link}>hrlama.ru/privacy.html</a> и
            в модальном окне на лендинге. Дата последнего обновления указана в начале документа.
          </p>

          {/* 17. КОНТАКТЫ */}
          <div style={styles.section}>
            <div style={styles.sectionIcon}><Key size={20} /></div>
            <h3 style={styles.sectionTitle}>17. Контактная информация</h3>
          </div>
          <ul style={styles.list}>
            <li><strong>Email по вопросам ПДн:</strong> <a href="mailto:info@hrlama.ru" style={styles.link}>info@hrlama.ru</a></li>
            <li><strong>Техническая поддержка:</strong> <a href="mailto:info@hrlama.ru" style={styles.link}>info@hrlama.ru</a></li>
            <li><strong>Юридический адрес Оператора:</strong> 197375, г. Санкт-Петербург, вн. тер. м.о. Коломяги, наб. Реки Каменки, д. 19, к. 1, стр. 1, кв. 341</li>
          </ul>

          <div style={styles.footerNote}>
            <p><strong>Реквизиты Оператора:</strong></p>
            <p>
              Индивидуальный предприниматель Наймушина Евгения Вадимовна<br />
              ИНН: 773379346085<br />
              ОГРНИП: 323784700299018<br />
              Адрес: 197375, г. Санкт-Петербург, вн. тер. м.о. Коломяги, наб. Реки Каменки, д. 19, к. 1, стр. 1, кв. 341<br />
              Email: info@hrlama.ru<br />
              Сайт: hrlama.ru
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  overlay: {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.6)', backdropFilter: 'blur(8px)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 1000, padding: '20px',
  },
  modal: {
    backgroundColor: '#ffffff', borderRadius: '24px', maxWidth: '800px',
    width: '100%', maxHeight: '85vh', display: 'flex', flexDirection: 'column',
    boxShadow: '0 30px 60px rgba(0, 0, 0, 0.3)',
    border: '1px solid rgba(106, 17, 203, 0.2)',
  },
  header: {
    display: 'flex', alignItems: 'center', gap: '16px',
    padding: '24px 32px', borderBottom: '1px solid rgba(0, 0, 0, 0.1)',
    background: 'linear-gradient(135deg, rgba(106, 17, 203, 0.05), rgba(37, 117, 252, 0.05))',
    borderRadius: '24px 24px 0 0',
  },
  title: { fontSize: '22px', fontWeight: 700, color: '#1a1a2e', flex: 1, margin: 0 },
  closeButton: {
    background: 'rgba(0, 0, 0, 0.05)', border: 'none', borderRadius: '50%',
    width: '36px', height: '36px', fontSize: '18px', cursor: 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#666',
  },
  content: { padding: '32px', overflowY: 'auto', flex: 1 },
  lastUpdated: { fontSize: '13px', color: '#999', marginBottom: '24px', fontStyle: 'italic' },
  section: { display: 'flex', alignItems: 'center', gap: '12px', marginTop: '32px', marginBottom: '16px', color: '#6a11cb' },
  sectionIcon: { display: 'flex', alignItems: 'center' },
  sectionTitle: { fontSize: '18px', fontWeight: 600, color: '#1a1a2e', margin: 0 },
  text: { fontSize: '15px', lineHeight: 1.7, color: '#444', marginBottom: '16px' },
  list: { paddingLeft: '24px', margin: '0 0 16px 0', fontSize: '15px', lineHeight: 1.8, color: '#444' },
  link: { color: '#2575fc', textDecoration: 'none' },
  footerNote: {
    marginTop: '32px', padding: '20px',
    background: 'rgba(106, 17, 203, 0.05)', borderRadius: '12px',
    fontSize: '14px', lineHeight: 1.8, color: '#555',
    border: '1px solid rgba(106, 17, 203, 0.1)',
  },
};

export default PrivacyPolicy;
